/**
 * FieldRecordsFooter (Unit 4 Phase 1) — the records screen's action bar.
 *
 * Until now the records screen had NO footer and no action bar of any kind:
 * FieldRecordsList was a context header plus one both-axis scroller, and Add
 * Tool lived in the header's right-hand slot. This is the new home for the
 * screen's verbs.
 *
 * "NO HANDLER, NO CONTROL" — the same contract the retired header Add button
 * used and the INV/ALL switch in the grid header uses (FieldRecordsList.jsx,
 * gated on onFieldViewChange): a slot renders only when its handler is
 * supplied. Deliberately NOT a disabled button — a greyed
 * control on a phone reads as "broken", invites a tap that does nothing, and
 * still costs its width. Search has no handler until Unit 6, so this phase
 * renders two slots and one divider, not three and two.
 *
 * Dividers are drawn BETWEEN RENDERED slots, never per-slot, so removing
 * Search cannot leave a trailing rule against the screen edge.
 *
 * Layout: a flex-none child of .frl, which is already a 100dvh flex column
 * with .frl-scroll as its flex:1 middle. The bar therefore sits at the bottom
 * of the screen WITHOUT position:fixed, and the scroller stops above it
 * instead of running underneath — no bottom-padding compensation to keep in
 * sync with the bar's height.
 */

import React from 'react';
import './FieldRecordsFooter.css';

/* Icons: 24-box, currentColor, stroke-width 2, round caps/joins — the field
   zone's established inline-SVG convention (FieldSyncChip.jsx:31-65). */

function EyeIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function PlusCircleIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="16" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

/* Unit 5 Phase 1. Sliders, because View is three CONTROLS over the same rows
   (filter / sort / group) rather than a lens onto them — an eye would collide
   with Columns, which already owns that metaphor here. Drawn to this file's
   convention rather than pulled from a package: no icon dependency exists in
   this zone and this phase does not add one. */
function SlidersIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="4" y1="21" x2="4" y2="14" />
      <line x1="4" y1="10" x2="4" y2="3" />
      <line x1="12" y1="21" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12" y2="3" />
      <line x1="20" y1="21" x2="20" y2="16" />
      <line x1="20" y1="12" x2="20" y2="3" />
      <line x1="1" y1="14" x2="7" y2="14" />
      <line x1="9" y1="8" x2="15" y2="8" />
      <line x1="17" y1="16" x2="23" y2="16" />
    </svg>
  );
}

export default function FieldRecordsFooter({ onColumns, onAdd, onSearch, onView }) {
  const slots = [];
  if (onColumns) {
    slots.push({
      id: 'columns', label: 'Columns', testId: 'field-footer-columns', onClick: onColumns, Icon: EyeIcon,
    });
  }
  if (onAdd) {
    slots.push({
      id: 'add', label: 'Add', testId: 'field-footer-add', onClick: onAdd, Icon: PlusCircleIcon,
    });
  }
  if (onSearch) {
    slots.push({
      id: 'search', label: 'Search', testId: 'field-footer-search', onClick: onSearch, Icon: SearchIcon,
    });
  }
  // Pushed LAST: the bar reads Columns · Add · Search · View. View is the
  // newest verb and the one a rep reaches for least often mid-walk, so it
  // takes the far edge rather than displacing three positions of muscle
  // memory the 08-25 device pass already built.
  if (onView) {
    slots.push({
      id: 'view', label: 'View', testId: 'field-footer-view', onClick: onView, Icon: SlidersIcon,
    });
  }

  // No handlers at all → no bar. An empty 56px rule across the bottom of the
  // screen is chrome that costs a rep vertical space and tells them nothing.
  if (slots.length === 0) return null;

  return (
    <nav className="frf" data-testid="field-records-footer" aria-label="Record actions">
      {slots.map(({ id, label, testId, onClick, Icon }, i) => (
        <React.Fragment key={id}>
          {i > 0 && <span className="frf-divider" aria-hidden="true" />}
          <button
            type="button"
            className="frf-btn"
            data-testid={testId}
            onClick={onClick}
          >
            <span className="frf-icon"><Icon /></span>
            <span className="frf-label">{label}</span>
          </button>
        </React.Fragment>
      ))}
    </nav>
  );
}
